import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Col, message, Row, Switch, Typography } from "antd";
import {
  fetchIndividualScenarios,
  updateMitigationStatus,
} from "../../services/scenarios";
import { queryKeys } from "../../constants";
import { useParams } from "react-router-dom";
import classes from "./MitigationPlan.module.scss";
import StatusDropDown from "../../components/StatusDropDown";
import MitigationPieChart from "./MitigationPieChart";
import MitigationDonutChart from "./MitigationDonutChart";
import { useState } from "react";

const { Text, Title } = Typography;

const MitigationPlan = () => {
  const { id } = useParams();
  const [selectedChart, setSelectedChart] = useState("pie");

  const handleChange = (checked) => {
    setSelectedChart(checked ? "pie" : "donut");
  };

  const getScenarioDetailsQuery = useQuery({
    queryKey: [queryKeys.mitigation, id],
    queryFn: () => fetchIndividualScenarios(id),
    enabled: true,
  });

  const updateMitigationStatusMutation = useMutation({
    mutationFn: updateMitigationStatus,
    onSuccess: () => {
      message.success("Mitigation has been updated successfully");
      getScenarioDetailsQuery.refetch();
    },
  });

  const graphData = [
    {
      name: "Cancelled Mitigation",
      y:
        getScenarioDetailsQuery.data?.data?.escalation_performance
          ?.cancelled_mitigations ?? 0,
    },
    {
      name: "Completed Mitigation",
      y:
        getScenarioDetailsQuery.data?.data?.escalation_performance
          ?.completed_mitigations ?? 0,
    },
    {
      name: "Escalated Mitigation",
      y:
        getScenarioDetailsQuery.data?.data?.escalation_performance
          ?.escalated_mitigations ?? 0,
    },
    {
      name: "On Hold Mitigation",
      y:
        getScenarioDetailsQuery.data?.data?.escalation_performance
          ?.on_hold_mitigations ?? 0,
    },
  ];

  const handleSelectStatus = (status, mitigation) => {
    updateMitigationStatusMutation.mutate({
      id: mitigation.id,
      postData: { status },
    });
  };

  const riskMitigation =
    getScenarioDetailsQuery.data?.data?.mitigations?.filter((r) => r.is_risk); // Filter out risk mitigation

  const opportunityMitigation =
    getScenarioDetailsQuery.data?.data?.mitigations?.filter(
      (r) => r.is_opportunity
    ); // Filter out opportunity mitigation

  return (
    <div>
      <Title level={3}>Mitigation Plan</Title>
      <Switch
        checkedChildren="Pie"
        unCheckedChildren="Donut"
        defaultChecked
        onChange={handleChange}
      />
      <div className={classes.container}>
        <Row>
          {selectedChart === "pie" ? (
            <Col md={24} sm={24} xs={24}>
              <MitigationPieChart graphData={graphData} />
            </Col>
          ) : (
            <Col md={24} sm={24} xs={24}>
              <MitigationDonutChart graphData={graphData} />
            </Col>
          )}
        </Row>
        <Alert
          message="Each mitigation action is assigned a priority level and clear
          timelines to facilitate progress tracking. Progress will be reviewed
          at regular intervals to adjust the mitigation plan based on real-time
          insights and changes in project dynamics."
          type="info"
          showIcon
        />

        {getScenarioDetailsQuery.data?.data?.mitigations?.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No Mitigation Plan Available
          </Text>
        ) : (
          <div className={classes.mitigationContainer}>
            <Row gutter={[16, 16]}>
              <Col md={12} sm={24}>
                <Title level={4} style={{ marginTop: 5 }}>
                  Risk Mitigation
                </Title>
                <div className={classes.wrapper}>
                  {riskMitigation?.map((mitigation) => (
                    <div className={classes.mitigationValueContainer}>
                      <div style={{ minWidth: "120px" }}>
                        <StatusDropDown
                          handleSelectStatus={(status) =>
                            handleSelectStatus(status, mitigation)
                          }
                          initialStatus={mitigation.status}
                        />
                      </div>
                      <div>
                        <Text key={mitigation.id} style={{ fontSize: 14 }}>
                          {mitigation.description}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
              <Col md={12} sm={24}>
                <Title level={4} style={{ marginTop: 5 }}>
                  Opportunity Mitigation
                </Title>
                <div className={classes.wrapper}>
                  {opportunityMitigation?.map((mitigation) => (
                    <div className={classes.mitigationValueContainer}>
                      <div style={{ minWidth: "120px" }}>
                        <StatusDropDown
                          handleSelectStatus={(status) =>
                            handleSelectStatus(status, mitigation)
                          }
                          initialStatus={mitigation.status}
                        />
                      </div>
                      <div>
                        <Text key={mitigation.id} style={{ fontSize: 14 }}>
                          {mitigation.description}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};
export default MitigationPlan;
